import { Avatar, Badge, Dropdown, Menu, Tooltip } from "antd";
import { Header } from "antd/lib/layout/layout";
import {
  MenuFoldOutlined,
  BellOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  LogoutOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  FileDoneOutlined,
  ArrowLeftOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import Notifications from "../Notifications";
import Search from "antd/lib/input/Search";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../features/AuthSlice";
import { userDetails } from "../../features/UserSlice";
import SsoSwitcher from "../common/SsoSwitcher";
import GetNotifcationsApi from "../../api/notifications/GetNotificationsApi";
import { SsoGenerateLinkTokenApi } from "../../api/auth/SsoApi";
import { positionDetails } from "../../features/PositionSlice";
import Theme from "../../theme/Theme";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";

function MainHeader(props) {
  const [notifications, setNotifications] = useState(false);
  const globalSettings = useGlobalSettings();
  const userState = useSelector((state) => state.userState.value);
  const setupAccountState = useSelector(
    (state) => state.setupAccountState.value
  );
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [notificationCount, setNotificationCount] = useState(0);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (positionState?.id) {
      getNotifications();
      
      // Set up polling for real-time notifications
      const notificationInterval = setInterval(() => {
        getNotifications();
      }, 30000); // Poll every 30 seconds
      
      // Cleanup interval on unmount or position change
      return () => {
        clearInterval(notificationInterval);
      };
    }
  }, [positionState]);

  // Listen for open-notifications event dispatched by RecentActivities widget
  useEffect(() => {
    const handler = () => setNotifications(true);
    window.addEventListener("open-notifications", handler);
    return () => window.removeEventListener("open-notifications", handler);
  }, []);

  // Refresh notifications when user returns to the app tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && positionState?.id) {
        getNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [positionState]);

  async function getNotifications() {
    const apiResponse = await GetNotifcationsApi(
      1,
      10,
      "UnRead",
      positionState?.id
    );
    if (apiResponse.success) {
      setNotificationCount(apiResponse.data?.totalUnRead);
    }
  }

  function toUserProfile() {
    if (setupAccountState) {
      navigate("/settings/user-profile");
    } else {
      navigate("/setup-account/user-profile");
    }
  }

  function toSettings() {
    navigate("/settings");
  }

  function logoutNow() {
    const PermissionService = require('../../services/PermissionService').default;
    
    // Clear all data
    localStorage.setItem("authToken", "");
    localStorage.setItem("authUser", "");
    localStorage.removeItem("stPosition");
    localStorage.removeItem("currentPositionId");
    
    // Clear permission cache
    PermissionService.clearCache();
    
    // Clear Redux state
    dispatch(logout());
    dispatch(userDetails({}));
    dispatch(positionDetails({}));
    
    navigate("/login");
  }

  function changePosition(position) {
    // Import PermissionService at the top if not already imported
    const PermissionService = require('../../services/PermissionService').default;
    
    // Clear old position cache
    const oldPositionId = localStorage.getItem("currentPositionId");
    if (oldPositionId) {
      PermissionService.clearCache(oldPositionId);
    }
    
    // Update position in Redux and localStorage
    dispatch(positionDetails(position));
    localStorage.setItem("stPosition", position.id);
    localStorage.setItem("currentPositionId", position.id);
    
    // Clear all permission cache to force refresh
    PermissionService.clearCache();
    
    // Navigate to home and force reload to refresh all permissions
    navigate("/");
    window.location.reload();
  }

  function renderPositions() {
    let output = [];
    userState?.positions?.forEach((position) => {
      output.push(
        <div
          onClick={() => changePosition(position)}
          key={position.id}
          className={
            "py-2 pl-5 cursor-pointer " +
            (positionState.id === position.id
              ? "bg-gray-200"
              : "hover:bg-gray-100")
          }
        >
          <div>{position.designation?.name}</div>
          <div className="text-xs text-gray-500">{position.unit?.name}</div>
        </div>
      );
    });
    return output;
  }

  const menu = (
    <Menu style={{ width: "280px" }}>
      <div className="p-2 text-center">
        {userState?.imageUrl ? (
          <Avatar
            size={64}
            className="cursor-pointer"
            src={userState?.imageUrl}
          />
        ) : (
          <Avatar size={64}>
            {userState?.firstName?.charAt(0).toUpperCase()}
          </Avatar>
        )}
        <div className="text-base mt-1">{userState?.firstName}</div>
      </div>
      <Menu.Divider />
      <div>
        <div className="ml-3">Positions</div>
        <div>{renderPositions()}</div>
      </div>

      <Menu.Divider />
      <Menu.Item key="0">
        <div className="flex items-center" onClick={() => toUserProfile()}>
          <UserOutlined className="w-4 mr-1" /> Your profile
        </div>
      </Menu.Item>
      {setupAccountState && (
        <Menu.Item key="1">
          <div className="flex items-center" onClick={() => toSettings()}>
            <SettingOutlined className="w-4 mr-1" /> Settings
          </div>
        </Menu.Item>
      )}
      <Menu.Item key="1">
        <div className="flex items-center">
          <QuestionCircleOutlined className="w-4 mr-1" /> Help and Support
        </div>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="3">
        <div className="flex items-center" onClick={() => logoutNow()}>
          <LogoutOutlined className="w-4 mr-1" /> Logout
        </div>
      </Menu.Item>
    </Menu>
  );

  const search = (
    <Menu style={{ padding: "8px" }}>
      <Search placeholder="input search text" style={{ width: "100%" }} />
    </Menu>
  );

  return (
    <>
      <Header
        className="site-layout-background flex items-center"
        style={{
          backgroundColor: Theme().background,
          paddingLeft: "24px",
          paddingRight: "24px",
          borderBottom: `2px solid ${Theme().navBorder}`,
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <div className="flex items-center">
            <div className="flex h-full text-lg mr-3">
              {props.menu ? (
                <MenuUnfoldOutlined
                  className="th-pri-hover"
                  onClick={() => props.openMenu()}
                />
              ) : (
                <MenuFoldOutlined
                  className="th-pri-hover"
                  onClick={() => props.openMenu()}
                />
              )}
              <ArrowLeftOutlined
                className="th-pri-hover ml-3"
                onClick={() => navigate(-1)}
              />
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex h-full md:hidden mr-3 sm:mr-5">
              <Dropdown
                placement="bottomRight"
                overlay={search}
                trigger={["click"]}
              >
                <SearchOutlined className="text-lg th-pri-hover" />
              </Dropdown>
            </div>
            {globalSettings.showSynergen !== false && (
            <Tooltip title={"Open SynergenHR"}>
              <SwapOutlined
                onClick={() => {
                  SsoGenerateLinkTokenApi().then(res => {
                    if (res && res.linkToken) {
                      window.open(`${process.env.REACT_APP_SYNERGEN_URL || 'http://localhost:8000'}/sso/login-from-strategizer/?token=${res.linkToken}`, '_blank');
                    } else {
                      window.open(process.env.REACT_APP_SYNERGEN_URL || 'http://localhost:8000', '_blank');
                    }
                  });
                }}
                className="mr-3 sm:mr-5 text-lg th-pri-hover"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
            )}
            <Tooltip title={"Approvals"}>
              <FileDoneOutlined
                onClick={() => navigate("/settings/target-approvals")}
                className="mr-3 sm:mr-5 text-lg th-pri-hover"
              />
            </Tooltip>
            <Tooltip title={"Notification"}>
              <Badge
                count={notificationCount && notificationCount}
                offset={[-18, 0]}
                overflowCount={9}
              >
                <BellOutlined
                  onClick={() => setNotifications(true)}
                  className="mr-3 sm:mr-5 text-lg th-pri-hover"
                />
              </Badge>
            </Tooltip>
            {setupAccountState && (
              <Tooltip title={"Settings"}>
                <SettingOutlined
                  onClick={() => toSettings()}
                  className="mr-3 sm:mr-5 text-lg th-pri-hover"
                />
              </Tooltip>
            )}
            <Dropdown
              placement="bottomRight"
              overlay={menu}
              trigger={["click"]}
              className={"cursor-pointer"}
            >
              {userState?.imageUrl ? (
                <Avatar
                  size={"large"}
                  className="cursor-pointer"
                  src={userState?.imageUrl}
                />
              ) : (
                <Avatar size={"large"}>
                  {userState?.firstName?.charAt(0).toUpperCase()}
                </Avatar>
              )}
            </Dropdown>
          </div>
        </div>
      </Header>
      <Notifications
        visible={notifications}
        onClose={() => setNotifications(false)}
        handleNotificationCount={() => getNotifications()}
      />
    </>
  );
}

export default MainHeader;
